import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Countdown from "../components/Countdown";
import { useReveal } from "../hooks/useReveal";
import { useSEO } from "../hooks/useSEO";

/* ── Count-up hook ────────────────────────────────────────────────────────── */
function useCountUp(to, duration = 1600, from = 0) {
  const [value, setValue] = useState(from);
  const [triggered, setTriggered] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting && !triggered) setTriggered(true); },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [triggered]);

  useEffect(() => {
    if (!triggered) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(from + Math.round(eased * (to - from)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [triggered, to, from, duration]);

  return { value, ref };
}

function toRoman(n) {
  if (n <= 0) return "";
  const vals = [1000,900,500,400,100,90,50,40,10,9,5,4,1];
  const syms = ["M","CM","D","CD","C","XC","L","XL","X","IX","V","IV","I"];
  let r = "";
  for (let i = 0; i < vals.length; i++) {
    while (n >= vals[i]) { r += syms[i]; n -= vals[i]; }
  }
  return r;
}

/* ── AnimatedStat ─────────────────────────────────────────────────────────── */
function AnimatedStat({ to, from = 0, label, roman, italic, index, total }) {
  const { value, ref } = useCountUp(to, 1600, from);
  const display = roman ? toRoman(value) : String(value);

  return (
    <div
      ref={ref}
      style={{
        padding: "48px 0",
        borderRight: index < total - 1 ? "1px solid #1c1c1c" : "none",
        paddingLeft: index === 0 ? 0 : "clamp(24px, 4vw, 56px)",
        paddingRight: index === total - 1 ? 0 : "clamp(24px, 4vw, 56px)",
      }}
    >
      <p
        className="font-serif"
        style={{
          fontSize: "clamp(42px, 6vw, 88px)",
          lineHeight: 0.95,
          letterSpacing: roman ? "0.02em" : "-0.02em",
          color: "#C9A961",
          fontStyle: italic ? "italic" : "normal",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {display}
      </p>
      <p className="label mt-4" style={{ color: "rgba(245,241,232,0.45)", fontSize: 11 }}>
        {label}
      </p>
    </div>
  );
}

/* ── Timeline ─────────────────────────────────────────────────────────────── */
const TIMELINE_STEPS = [
  {
    label: "Annonce officielle",
    date: "Janv. 2026",
    done: true,
    desc: "Lancement public de la première édition du Stand d'Or.",
  },
  {
    label: "Sélection du jury",
    date: "Avr. 2026",
    done: true,
    active: true,
    desc: "Constitution du collège indépendant de neuf membres. Délibérations en cours.",
  },
  {
    label: "Nominations",
    date: "1er sept. 2026",
    done: false,
    desc: "Révélation simultanée des nominé·e·s dans les 13 catégories.",
  },
  {
    label: "Vote du public",
    date: "Sept.–Oct. 2026",
    done: false,
    desc: "Ouverture du vote pour le Prix du Public et le Prix de la Critique.",
  },
  {
    label: "Délibérations",
    date: "Nov. 2026",
    done: false,
    desc: "Le jury délibère à huis clos. Les résultats sont scellés.",
  },
  {
    label: "Cérémonie",
    date: "14 nov. 2026",
    done: false,
    desc: "Remise des trophées. Première cérémonie officielle du Stand d'Or.",
  },
];

function Timeline() {
  const [hovered, setHovered] = useState(null);
  const sectionRef = useReveal();
  const doneCount = TIMELINE_STEPS.filter((s) => s.done).length;
  const progressPct = ((doneCount - 1) / (TIMELINE_STEPS.length - 1)) * 100;

  return (
    <section
      ref={sectionRef}
      className="reveal"
      style={{ background: "#000", borderTop: "1px solid #1c1c1c", borderBottom: "1px solid #1c1c1c" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-4">
          <p className="label" style={{ color: "#C9A961" }}>Où en sommes-nous</p>
          <p className="label" style={{ color: "rgba(245,241,232,0.2)", fontSize: 10 }}>
            Édition 2026 — {doneCount}/{TIMELINE_STEPS.length} étapes
          </p>
        </div>

        <div style={{ overflowX: "auto", paddingBottom: 8 }}>
          <div style={{ minWidth: 680, position: "relative" }}>
            {/* Line */}
            <div
              style={{
                position: "absolute",
                top: 13,
                left: `calc(100% / ${TIMELINE_STEPS.length * 2})`,
                right: `calc(100% / ${TIMELINE_STEPS.length * 2})`,
                height: 1,
                zIndex: 0,
                background: "#1c1c1c",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  width: `${progressPct}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, rgba(201,169,97,0.8), rgba(201,169,97,0.3))",
                }}
              />
            </div>

            {/* Steps */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              {TIMELINE_STEPS.map((step, i) => (
                <div
                  key={i}
                  style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative", zIndex: 1 }}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {/* Tooltip */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: "calc(100% + 20px)",
                      left: "50%",
                      transform: `translateX(-50%) translateY(${hovered === i ? 0 : 6}px)`,
                      width: 178,
                      background: "#080807",
                      border: "1px solid #1c1c1c",
                      borderTop: "1px solid rgba(201,169,97,0.25)",
                      padding: "12px 14px",
                      zIndex: 20,
                      opacity: hovered === i ? 1 : 0,
                      pointerEvents: "none",
                      transition: "opacity 0.25s ease, transform 0.25s ease",
                    }}
                  >
                    <p className="label" style={{ fontSize: 8, color: "#C9A961", marginBottom: 5 }}>
                      {step.date}
                    </p>
                    <p style={{ color: "rgba(248,246,240,0.7)", fontSize: 12, lineHeight: 1.6, fontFamily: "Inter, sans-serif" }}>
                      {step.desc}
                    </p>
                    {/* Arrow */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: -5,
                        left: "50%",
                        transform: "translateX(-50%) rotate(45deg)",
                        width: 8,
                        height: 8,
                        background: "#080807",
                        borderRight: "1px solid #1c1c1c",
                        borderBottom: "1px solid #1c1c1c",
                      }}
                    />
                  </div>

                  {/* Dot */}
                  <div style={{ position: "relative", marginBottom: 18 }}>
                    {step.active && (
                      <>
                        <div
                          className="timeline-pulse"
                          style={{
                            position: "absolute",
                            inset: -8,
                            borderRadius: "50%",
                            border: "1px solid rgba(201,169,97,0.22)",
                          }}
                        />
                        <div
                          className="timeline-pulse"
                          style={{
                            position: "absolute",
                            inset: -4,
                            borderRadius: "50%",
                            border: "1px solid rgba(201,169,97,0.18)",
                            animationDelay: "0.7s",
                          }}
                        />
                      </>
                    )}
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        background: step.active
                          ? "#C9A961"
                          : step.done
                            ? "rgba(201,169,97,0.1)"
                            : "#050505",
                        border: `1px solid ${
                          step.active
                            ? "#C9A961"
                            : step.done
                              ? "rgba(201,169,97,0.45)"
                              : hovered === i
                                ? "rgba(201,169,97,0.2)"
                                : "#1c1c1c"
                        }`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.35s ease",
                      }}
                    >
                      {step.done && !step.active && (
                        <svg width="10" height="7" viewBox="0 0 10 7" fill="none">
                          <path d="M1 3.5L3.8 6.5L9 1" stroke="#C9A961" strokeWidth="1.5" strokeLinecap="square" />
                        </svg>
                      )}
                      {step.active && (
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#0A0908" }} />
                      )}
                    </div>
                  </div>

                  {/* Label */}
                  <p
                    className="label"
                    style={{
                      fontSize: 9,
                      letterSpacing: "0.12em",
                      color: step.active
                        ? "#C9A961"
                        : step.done
                          ? "rgba(201,169,97,0.45)"
                          : hovered === i
                            ? "rgba(248,246,240,0.4)"
                            : "rgba(248,246,240,0.14)",
                      textAlign: "center",
                      maxWidth: 84,
                      lineHeight: 1.5,
                      transition: "color 0.3s ease",
                    }}
                  >
                    {step.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Carrousel spectacles ────────────────────────────────────────────────── */
const SOCIAL_ICONS = {
  instagram: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
  ),
  tiktok: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.75a8.27 8.27 0 004.84 1.56V6.86a4.85 4.85 0 01-1.07-.17z"/>
    </svg>
  ),
  youtube: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
    </svg>
  ),
};

function ShowCard({ show, active, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flexShrink: 0,
        width: active ? 260 : 200,
        cursor: "pointer",
        transition: "width 0.5s cubic-bezier(0.22,1,0.36,1)",
        position: "relative",
      }}
    >
      {/* Cover */}
      <div
        style={{
          aspectRatio: "3 / 4",
          overflow: "hidden",
          position: "relative",
          border: `1px solid ${active ? "rgba(201,169,97,0.5)" : "#1a1a1a"}`,
          transition: "border-color 0.4s ease",
        }}
      >
        {show.cover_image ? (
          <img
            src={show.cover_image}
            alt={show.title}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: active || hovered ? "grayscale(20%)" : "grayscale(80%)",
              transform: hovered ? "scale(1.04)" : "scale(1)",
              transition: "filter 0.6s ease, transform 0.8s ease",
            }}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "#0d0d0d", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <p className="label" style={{ fontSize: 9, color: "#2a2a2a", textAlign: "center", padding: "0 16px" }}>{show.title}</p>
          </div>
        )}

        {/* Active gold bar */}
        <div style={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: 2,
          background: "#C9A961",
          opacity: active ? 1 : 0,
          transition: "opacity 0.4s ease",
        }} />

        {/* Hover overlay avec liens */}
        <div style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.75)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: 16,
          gap: 10,
          opacity: (hovered || active) ? 1 : 0,
          transition: "opacity 0.35s ease",
        }}>
          {show.ticketing_url && (
            <a
              href={show.ticketing_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="label"
              style={{
                width: "100%",
                textAlign: "center",
                background: "#C9A961",
                color: "#0A0908",
                padding: "8px 0",
                fontSize: 9,
                letterSpacing: "0.2em",
                textDecoration: "none",
                display: "block",
              }}
            >
              Billetterie →
            </a>
          )}
          <div style={{ display: "flex", gap: 12 }}>
            {["instagram", "tiktok", "youtube"].map((net) =>
              show[net] ? (
                <a
                  key={net}
                  href={show[net]}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: "rgba(245,241,232,0.6)", transition: "color 0.3s" }}
                  onMouseOver={(e) => (e.currentTarget.style.color = "#C9A961")}
                  onMouseOut={(e) => (e.currentTarget.style.color = "rgba(245,241,232,0.6)")}
                >
                  {SOCIAL_ICONS[net]}
                </a>
              ) : null
            )}
          </div>
        </div>
      </div>

      {/* Label */}
      <div className="mt-4">
        <p className="label" style={{ fontSize: 9, color: active ? "#C9A961" : "rgba(245,241,232,0.3)", letterSpacing: "0.15em", transition: "color 0.4s", marginBottom: 4 }}>
          {show.artist_name.toUpperCase()}
        </p>
        <p className="font-serif" style={{ fontSize: "clamp(14px, 1.4vw, 18px)", color: active ? "#F8F6F0" : "rgba(245,241,232,0.4)", lineHeight: 1.15, transition: "color 0.4s" }}>
          {show.title}
        </p>
      </div>
    </div>
  );
}

function ShowsCarousel({ shows }) {
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const sectionRef = useReveal();

  const prev = () => setActive((i) => Math.max(0, i - 1));
  const next = () => setActive((i) => Math.min(shows.length - 1, i + 1));

  if (!shows.length) return null;

  return (
    <section
      ref={sectionRef}
      className="reveal"
      style={{ borderTop: "1px solid #1c1c1c", background: "#030303", overflow: "hidden" }}
      data-testid="home-shows"
    >
      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 pb-8 flex items-center justify-between">
        <p className="label" style={{ color: "#C9A961" }}>Les spectacles</p>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={prev}
            disabled={active === 0}
            aria-label="Précédent"
            style={{ background: "transparent", border: "1px solid #2a2a2a", color: active === 0 ? "#1c1c1c" : "#F8F6F0", width: 36, height: 36, cursor: active === 0 ? "default" : "pointer", transition: "color 0.3s, border-color 0.3s", display: "flex", alignItems: "center", justifyContent: "center" }}
            onMouseOver={(e) => { if (active > 0) { e.currentTarget.style.borderColor = "#C9A961"; e.currentTarget.style.color = "#C9A961"; } }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = "#2a2a2a"; e.currentTarget.style.color = active === 0 ? "#1c1c1c" : "#F8F6F0"; }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8 1L3 6L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/></svg>
          </button>
          <button
            onClick={next}
            disabled={active === shows.length - 1}
            aria-label="Suivant"
            style={{ background: "transparent", border: "1px solid #2a2a2a", color: active === shows.length - 1 ? "#1c1c1c" : "#F8F6F0", width: 36, height: 36, cursor: active === shows.length - 1 ? "default" : "pointer", transition: "color 0.3s, border-color 0.3s", display: "flex", alignItems: "center", justifyContent: "center" }}
            onMouseOver={(e) => { if (active < shows.length - 1) { e.currentTarget.style.borderColor = "#C9A961"; e.currentTarget.style.color = "#C9A961"; } }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = "#2a2a2a"; e.currentTarget.style.color = active === shows.length - 1 ? "#1c1c1c" : "#F8F6F0"; }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M4 1L9 6L4 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/></svg>
          </button>
        </div>
        <Link to="/spectacles" className="label" style={{ color: "rgba(201,169,97,0.6)", borderBottom: "1px solid rgba(201,169,97,0.2)", paddingBottom: 2, fontSize: 10 }}>
          Voir tous →
        </Link>
      </div>

      {/* Track */}
      <div
        ref={trackRef}
        style={{
          display: "flex",
          gap: 24,
          padding: "0 clamp(24px, 5vw, 96px) 48px",
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {shows.map((show, i) => (
          <ShowCard key={show.id} show={show} active={i === active} onClick={() => setActive(i)} />
        ))}
      </div>
    </section>
  );
}

/* ── Page principale ─────────────────────────────────────────────────────── */
const STATS = [
  { to: 13, label: "Catégories" },
  { to: 9, label: "Membres du jury", roman: true, italic: true },
  { to: 1, label: "Cérémonie", roman: true, italic: true },
  { to: 2026, from: 2020, label: "Première édition" },
];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [jury, setJury] = useState([]);
  const [shows, setShows] = useState([]);
  const [ceremony, setCeremony] = useState(null);
  const [hoverCat, setHoverCat] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, j, ce, p] = await Promise.all([
          api.get("/categories"),
          api.get("/jury"),
          api.get("/ceremony"),
          api.get("/shows"),
        ]);
        setCategories(c.data);
        setJury(j.data);
        setCeremony(ce.data);
        setShows(p.data || []);
      } catch { /* noop */ }
    })();
  }, []);

  useSEO(null, "Première cérémonie officielle des awards de l'humour francophone. Nominations, jury indépendant, cérémonie 2026.");

  const visionRef = useReveal();
  const statsRef = useReveal();
  const categoriesRef = useReveal();
  const manifesteRef = useReveal();
  const juryRef = useReveal();
  const ceremonyRef = useReveal();
  const partnersRef = useReveal();

  const carouselShows = shows.slice(0, 8);

  return (
    <div data-testid="home-page">
      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden grain"
        style={{ minHeight: "100vh", background: "#000" }}
        data-testid="home-hero"
      >
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1540397521216-0b56cdc2f177?auto=format&fit=crop&q=80&w=2000)",
            filter: "grayscale(100%) brightness(0.28)",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(10,10,10,0.65) 55%, #0A0A0A 100%)" }}
          aria-hidden="true"
        />
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 pt-[180px] pb-24 min-h-screen flex flex-col justify-between">
          <div>
            <p className="label mb-10" style={{ color: "#C9A961" }} data-testid="hero-edition">
              Édition&nbsp;2026 — Première cérémonie officielle
            </p>
            <h1
              className="font-serif"
              style={{ fontSize: "clamp(68px, 14vw, 220px)", lineHeight: 0.88, letterSpacing: "-0.035em", color: "#F8F6F0" }}
              data-testid="hero-title"
            >
              Le Stand
              <br />
              <em style={{ color: "#C9A961" }}>d'Or</em>
            </h1>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mt-24 items-end">
            <div className="md:col-span-7">
              <p
                className="font-serif"
                style={{ color: "#F8F6F0", fontSize: "clamp(20px, 2.2vw, 28px)", lineHeight: 1.35, maxWidth: 640 }}
                data-testid="hero-subtitle"
              >
                Première cérémonie des awards de l'humour francophone. Une institution pour un art longtemps resté sans reconnaissance officielle.
              </p>
            </div>
            <div className="md:col-span-5 md:text-right flex md:justify-end">
              <Link to="/nominations" className="btn-ghost" data-testid="hero-cta">
                Découvrir les nominations <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CHIFFRES ANIMÉS ───────────────────────────────────────────────── */}
      <section
        ref={statsRef}
        className="reveal"
        style={{ background: "#000", borderTop: "1px solid #1c1c1c", borderBottom: "1px solid #1c1c1c" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {STATS.map((s, i) => (
              <AnimatedStat
                key={s.label}
                to={s.to}
                from={s.from || 0}
                label={s.label}
                roman={s.roman}
                italic={s.italic}
                index={i}
                total={STATS.length}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── TIMELINE ──────────────────────────────────────────────────────── */}
      <Timeline />

      {/* ── VISION ────────────────────────────────────────────────────────── */}
      <section
        ref={visionRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-32 md:py-48"
        data-testid="home-vision"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="label mb-8" style={{ color: "#C9A961" }}>I. La vision</p>
            <p className="font-serif" style={{ fontSize: "clamp(32px, 4vw, 56px)", lineHeight: 1.08, color: "#F8F6F0" }}>
              « Le rire est un art. Il mérite une institution. »
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6" style={{ color: "#F8F6F0", fontSize: 17, lineHeight: 1.75 }}>
            <p>
              Le Stand d'Or consacre, pour la première fois, le stand-up francophone comme une discipline majeure de la
              scène contemporaine. À rebours des formats télévisés et des trophées commerciaux, l'institution propose
              une reconnaissance par les pairs : metteurs en scène, programmateurs, critiques, autrices et auteurs.
            </p>
            <p style={{ color: "rgba(248,246,240,0.65)" }}>
              Treize catégories. Un jury indépendant. Une cérémonie annuelle. Et, à travers elles, la conviction qu'un
              texte comique, une mise en scène, une écriture numérique, une voix nouvelle méritent la même rigueur
              critique que le cinéma, la musique ou le théâtre.
            </p>
            <Link
              to="/manifeste"
              className="label inline-block pt-4"
              style={{ color: "#C9A961", borderBottom: "1px solid rgba(201,169,97,0.4)", paddingBottom: 3 }}
            >
              Lire le manifeste →
            </Link>
          </div>
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* ── CATEGORIES ────────────────────────────────────────────────────── */}
      <section
        ref={categoriesRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-32 md:py-48"
        data-testid="home-categories"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
          <div className="md:col-span-5">
            <p className="label mb-6" style={{ color: "#C9A961" }}>II. Les catégories</p>
            <h2 className="font-serif" style={{ fontSize: "clamp(40px, 5vw, 72px)", lineHeight: 1.02, color: "#F8F6F0" }}>
              Treize distinctions, une cartographie de l'humour contemporain.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p style={{ color: "rgba(248,246,240,0.65)", fontSize: 16, lineHeight: 1.75 }}>
              De la révélation au prix d'honneur, chaque catégorie a été pensée pour refléter la pluralité des formes
              prises par le stand-up aujourd'hui — scène, écriture, mise en scène, numérique, audio.
            </p>
          </div>
        </div>

        {categories.length > 0 ? (
          <ul style={{ borderTop: "1px solid #1c1c1c" }}>
            {categories.map((cat) => (
              <li
                key={cat.slug}
                className="grid grid-cols-12 gap-6 py-8 md:py-10"
                onMouseEnter={() => setHoverCat(cat.slug)}
                onMouseLeave={() => setHoverCat(null)}
                style={{ borderBottom: "1px solid #1c1c1c", cursor: "default" }}
                data-testid={`category-row-${cat.slug}`}
              >
                <span
                  className="col-span-2 md:col-span-1 label pt-3"
                  style={{ color: hoverCat === cat.slug ? "#C9A961" : "#3a3a3a", transition: "color 0.4s" }}
                >
                  {String(cat.order).padStart(2, "0")}
                </span>
                <span
                  className="col-span-10 md:col-span-5 font-serif"
                  style={{
                    fontSize: "clamp(28px, 3.5vw, 48px)",
                    color: hoverCat === cat.slug ? "#C9A961" : "#F8F6F0",
                    lineHeight: 1.05,
                    transition: "color 0.4s",
                  }}
                >
                  {cat.title}
                </span>
                <span
                  className="col-span-12 md:col-span-6 pt-3 md:pt-4"
                  style={{ color: "rgba(248,246,240,0.6)", fontSize: 15, lineHeight: 1.65 }}
                >
                  {hoverCat === cat.slug ? cat.long_description : cat.description}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ borderTop: "1px solid #1c1c1c", paddingTop: 48 }}>
            <p
              className="font-serif italic"
              style={{ fontSize: "clamp(22px, 2.5vw, 34px)", color: "rgba(248,246,240,0.25)", maxWidth: 500 }}
            >
              Les catégories seront annoncées prochainement.
            </p>
          </div>
        )}
      </section>

      {/* ── CARROUSEL SPECTACLES ──────────────────────────────────────────── */}
      {carouselShows.length > 0 && (
        <>
          <div className="hairline max-w-[1400px] mx-auto" />
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-2">
            <p className="label mb-2" style={{ color: "#C9A961" }}>III. Les spectacles en cours</p>
            <h2 className="font-serif" style={{ fontSize: "clamp(36px, 4.5vw, 64px)", lineHeight: 1.04, color: "#F8F6F0" }}>
              Sur scène en ce moment.
            </h2>
          </div>
          <ShowsCarousel shows={carouselShows} />
        </>
      )}

      {/* ── MANIFESTE ─────────────────────────────────────────────────────── */}
      <section
        ref={manifesteRef}
        className="reveal"
        style={{ background: "#000", borderTop: "1px solid #1c1c1c" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32 md:py-48">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-1">
              <p
                className="font-serif"
                style={{
                  writingMode: "vertical-lr",
                  transform: "rotate(180deg)",
                  fontSize: 11,
                  letterSpacing: "0.25em",
                  color: "rgba(201,169,97,0.4)",
                  textTransform: "uppercase",
                  userSelect: "none",
                }}
                aria-hidden="true"
              >
                Manifeste
              </p>
            </div>
            <div className="md:col-span-8 md:col-start-3">
              <p className="label mb-10" style={{ color: "#C9A961" }}>
                {carouselShows.length > 0 ? "IV." : "III."} Le manifeste
              </p>
              <blockquote>
                <p
                  className="font-serif"
                  style={{ fontSize: "clamp(28px, 4vw, 60px)", lineHeight: 1.12, color: "#F8F6F0", letterSpacing: "-0.01em" }}
                >
                  « L'humour n'est pas un divertissement mineur. C'est une forme d'intelligence du monde, une manière de tenir debout face à ce qui résiste. »
                </p>
              </blockquote>
              <div className="mt-12 flex items-center gap-8">
                <div style={{ width: 40, height: 1, background: "#C9A961", opacity: 0.5 }} aria-hidden="true" />
                <Link
                  to="/manifeste"
                  className="label"
                  style={{ color: "#C9A961", borderBottom: "1px solid rgba(201,169,97,0.4)", paddingBottom: 3 }}
                >
                  Lire les sept articles fondateurs →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── JURY ──────────────────────────────────────────────────────────── */}
      <section
        ref={juryRef}
        className="reveal max-w-[1400px] mx-auto px-6 lg:px-12 py-32 md:py-48"
        style={{ borderTop: "1px solid #1c1c1c" }}
        data-testid="home-jury"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
          <div className="md:col-span-5">
            <p className="label mb-6" style={{ color: "#C9A961" }}>
              {carouselShows.length > 0 ? "V." : "IV."} Le jury
            </p>
            <h2 className="font-serif" style={{ fontSize: "clamp(40px, 5vw, 72px)", lineHeight: 1.02, color: "#F8F6F0" }}>
              Un collège indépendant, réuni à huis clos.
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p style={{ color: "rgba(248,246,240,0.65)", fontSize: 16, lineHeight: 1.75 }}>
              Neuf personnalités issues de la mise en scène, de la programmation, de la critique, de l'écriture. Une présidence rotative. Un vote à bulletin unique.
            </p>
          </div>
        </div>

        {(() => {
          const JURY_REVEAL = new Date("2026-11-09T20:00:00+01:00");
          const juryRevealed = Date.now() >= JURY_REVEAL.getTime() && jury.length > 0;
          return juryRevealed ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-14">
              {jury.map((j) => (
                <figure key={j.id} className="flex flex-col" data-testid={`jury-card-${j.id}`}>
                  <div className="relative overflow-hidden" style={{ aspectRatio: "3 / 4", background: "#111" }}>
                    <img
                      src={j.portrait}
                      alt={j.name}
                      className="bw-image w-full h-full object-cover"
                      loading="lazy"
                      onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                      onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")}
                    />
                  </div>
                  <figcaption className="mt-6">
                    <p className="font-serif" style={{ fontSize: 22, color: "#F8F6F0", lineHeight: 1.15 }}>{j.name}</p>
                    <p className="label mt-2" style={{ color: "#A1A1AA" }}>{j.role}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : (
            <div style={{ borderTop: "1px solid #1c1c1c", paddingTop: 48 }}>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-8">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="flex flex-col">
                    <div
                      style={{
                        aspectRatio: "3 / 4",
                        background: "#070706",
                        border: "1px solid #1a1a1a",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 12,
                        }}
                      >
                        <div style={{ border: "1px solid rgba(201,169,97,0.2)", padding: "4px 12px" }}>
                          <p className="label" style={{ fontSize: 8, letterSpacing: "0.3em", color: "rgba(201,169,97,0.4)" }}>
                            CONFIDENTIEL
                          </p>
                        </div>
                        <p
                          className="font-serif"
                          style={{ fontSize: 13, color: "rgba(201,169,97,0.5)", letterSpacing: "0.05em", textAlign: "center", lineHeight: 1.4 }}
                        >
                          DÈS LE<br />14 SEPT.
                        </p>
                      </div>
                    </div>
                    <p className="label mt-4" style={{ color: "#1e1e1e", fontSize: 9, letterSpacing: "0.2em" }}>
                      IDENTITÉ SCELLÉE
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-12">
                <Link
                  to="/jury"
                  className="label"
                  style={{ color: "#C9A961", borderBottom: "1px solid rgba(201,169,97,0.4)", paddingBottom: 3 }}
                >
                  Voir la page jury →
                </Link>
              </div>
              <p
                className="font-serif italic mt-8"
                style={{ fontSize: "clamp(18px, 2vw, 28px)", color: "rgba(248,246,240,0.2)", maxWidth: 500 }}
              >
                Le jury se dévoile à partir du 14 septembre 2026.
              </p>
            </div>
          );
        })()}
      </section>

      {/* ── CEREMONY ──────────────────────────────────────────────────────── */}
      <section
        ref={ceremonyRef}
        className="reveal relative overflow-hidden grain"
        style={{ background: "#000", borderTop: "1px solid #1c1c1c" }}
        data-testid="home-ceremony"
      >
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(201,169,97,0.04) 0%, transparent 60%)" }}
          aria-hidden="true"
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-32 md:py-48">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <p className="label mb-6" style={{ color: "#C9A961" }}>
                {carouselShows.length > 0 ? "VI." : "V."} La cérémonie
              </p>
              <h2 className="font-serif" style={{ fontSize: "clamp(40px, 5vw, 80px)", lineHeight: 1.02, color: "#F8F6F0" }}>
                {ceremony?.date_iso
                  ? new Date(ceremony.date_iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
                  : "14 novembre 2026"}
              </h2>
              {ceremony?.city && (
                <p className="label mt-4" style={{ color: "rgba(245,241,232,0.45)" }}>
                  {ceremony.city}{ceremony.venue ? ` — ${ceremony.venue}` : ""}
                </p>
              )}
            </div>
            <div className="md:col-span-6 md:col-start-7 flex flex-col justify-between gap-12">
              <p style={{ color: "rgba(248,246,240,0.65)", fontSize: 17, lineHeight: 1.75 }}>
                La cérémonie inaugurale se tiendra à huis clos artistique, suivie d'une captation diffusée en différé
                par un partenaire francophone. L'ensemble des lauréats sera révélé ce soir-là.
              </p>
              {ceremony?.broadcast_platform && (
                <p className="label" style={{ color: "rgba(248,246,240,0.45)" }}>
                  Diffusion en direct —{" "}
                  {ceremony.broadcast_url ? (
                    <a
                      href={ceremony.broadcast_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#C9A961", borderBottom: "1px solid #C9A961" }}
                    >
                      {ceremony.broadcast_platform}
                    </a>
                  ) : (
                    <span style={{ color: "#C9A961" }}>{ceremony.broadcast_platform}</span>
                  )}
                </p>
              )}
              {ceremony && <Countdown targetIso={ceremony.date_iso} />}
            </div>
          </div>
        </div>
      </section>

      {/* ── PARTNERS BANDEAU ──────────────────────────────────────────────── */}
      <section
        ref={partnersRef}
        className="reveal"
        style={{
          borderTop: "1px solid #1c1c1c",
          borderBottom: "1px solid #1c1c1c",
          background: "#000",
          position: "relative",
          height: 100,
          overflow: "hidden",
        }}
        data-testid="home-partners"
      >
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", display: "flex", alignItems: "center" }}>
          <div className="marquee-track" style={{ display: "flex", whiteSpace: "nowrap", width: "max-content" }}>
            {Array.from({ length: 2 }).map((_, ri) => (
              <span key={ri} style={{ display: "flex", alignItems: "center" }}>
                {[
                  "Partenariat officiel",
                  "Édition MMXXVI",
                  "Associez votre marque à une institution naissante",
                  "Un nombre restreint de partenaires fondateurs",
                  "Collaborations sur-mesure",
                  "Le Stand d'Or — Première cérémonie de l'humour francophone",
                ].map((text, i) => (
                  <span key={i} style={{ display: "inline-flex", alignItems: "center" }}>
                    <span
                      className="label"
                      style={{ fontSize: 10, letterSpacing: "0.22em", color: "rgba(245,241,232,0.18)", paddingRight: 48 }}
                    >
                      {text}
                    </span>
                    <span style={{ color: "rgba(201,169,97,0.15)", marginRight: 48, fontSize: 7 }}>◆</span>
                  </span>
                ))}
              </span>
            ))}
          </div>
          <div
            className="absolute inset-y-0 left-0 w-40 pointer-events-none"
            style={{ background: "linear-gradient(90deg, #000, transparent)" }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-y-0 right-0 w-40 pointer-events-none"
            style={{ background: "linear-gradient(-90deg, #000, transparent)" }}
            aria-hidden="true"
          />
        </div>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Link
            to="/partenaires"
            className="btn-ghost"
            style={{ fontSize: 10, padding: "10px 28px", letterSpacing: "0.2em", position: "relative", zIndex: 2 }}
          >
            Devenir partenaire →
          </Link>
        </div>
      </section>
    </div>
  );
}
